/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { Component, inject, OnInit, signal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../auth.service';
import { SnackBarService } from '../../components/snack-bar/snack-bar.service';
import { Subrogation } from '../../models/subrogation/subrogation.interface';
import { SubrogationService } from '../subrogation.service';
import { DialogHeaderComponent } from '../../../../lib/components/dialog/dialog-header/dialog-header.component';
import { StepperComponent } from '../../components/stepper/stepper.component';
import { CdkStep } from '@angular/cdk/stepper';
import { InputComponent } from '../../../../lib/components/input/input.component';
import { SelectComponent } from '../../../../lib/components/select/select.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

interface SubrogationForm {
  emailFirstPart: string | null;
  domain: string | null;
}

@Component({
  selector: 'vitamui-common-subrogation-modal',
  templateUrl: './subrogation-modal.component.html',
  styleUrls: ['./subrogation-modal.component.scss'],
  imports: [
    DialogHeaderComponent,
    StepperComponent,
    CdkStep,
    MatDialogContent,
    FormField,
    InputComponent,
    SelectComponent,
    MatDialogActions,
    MatProgressSpinner,
    TranslatePipe,
  ],
})
export class SubrogationModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<SubrogationModalComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private readonly authService = inject(AuthService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly subrogationService = inject(SubrogationService);

  public stepIndex = signal(0);
  public domains: string[];
  public customerId: string;
  public user: { email: string; firstname: string; lastname: string };
  public readonly form = form(signal<SubrogationForm>({ emailFirstPart: null, domain: null }), (path) => {
    required(path.emailFirstPart);
    required(path.domain);
  });
  public pending: boolean;

  private subrogation: Subrogation;

  ngOnInit() {
    this.domains = this.data.domains;
    this.customerId = this.data.customerId;
    this.user = this.data.user;
    if (this.user) {
      this.stepIndex.set(1);
      this.retrieveOrCreateSubrogation(this.user.email);
    } else {
      this.form.domain().value.set(this.domains[0]);
    }
  }

  // check if a subrogation exists and was already created with the same information
  // a. the subrogation exists, we retrieve it
  // b. we asks for a subrogation
  retrieveOrCreateSubrogation(surrogateEmail: string) {
    this.pending = true;
    this.subrogationService.checkCurrentUserIsInSubrogation().subscribe((response: Subrogation) => {
      if (response.id) {
        if (response.surrogate === surrogateEmail) {
          this.stepIndex.set(1);
          // TODO subscribe to the modal close event and cancel the subrogation
          this.pending = true;
          this.subrogation = response;
          if (this.subrogation && this.subrogation.status === 'ACCEPTED') {
            this.logoutAndLaunchSubrogation(this.subrogation);
          } else {
            this.subrogationService.checkSubrogationStatus(this.subrogation, this.dialogRef).subscribe(() => {
              this.logoutAndLaunchSubrogation(this.subrogation);
            });
          }
        } else {
          this.dialogRef.close();
          this.snackBarService.open({
            message: 'SUBROGATION.HOME.RESULTS_TABLE.MODAL.ACTIVE_SUBROGATION',
            icon: 'vitamui-icon-link banner-icon',
            translateParams: { email: response.surrogate },
            translate: true,
          });
        }
      } else {
        this.createSubrogation(surrogateEmail);
      }
    });
  }

  askForSubrogation() {
    const { emailFirstPart, domain } = this.form().value();
    const surrogateEmail = emailFirstPart + '@' + domain;
    this.user = { email: surrogateEmail, firstname: undefined, lastname: undefined };
    this.retrieveOrCreateSubrogation(surrogateEmail);
  }

  createSubrogation(surrogateEmail: string) {
    // TODO subscribe to the modal close event and cancel the subrogation
    this.pending = true;
    const currentUserEmail = this.authService.user.email;
    const currentUserCustomerId = this.authService.user.customerId;
    this.subrogation = {
      id: null,
      status: 'CREATED',
      date: null,
      surrogate: surrogateEmail,
      superUser: currentUserEmail,
      surrogateCustomerId: this.customerId,
      superUserCustomerId: currentUserCustomerId,
    };
    this.subrogationService.createSubrogation(this.subrogation).subscribe(
      (subrogation) => {
        this.stepIndex.set(1);
        this.subrogation = subrogation;
        this.subrogationService.checkSubrogationStatus(this.subrogation, this.dialogRef).subscribe(() => {
          this.logoutAndLaunchSubrogation(this.subrogation);
        });
      },
      () => {
        this.handleSubrogationError();
      },
    );
  }

  handleSubrogationError() {
    this.dialogRef.close();
    this.snackBarService.open({
      message: 'SUBROGATION.HOME.RESULTS_TABLE.MODAL.IMPOSSIBLE_SUBROGATION',
      icon: 'vitamui-icon-link banner-icon',
    });
  }

  logoutAndLaunchSubrogation(subrogation: Subrogation) {
    this.dialogRef.close();
    this.authService.logoutForSubrogation(
      subrogation.superUser,
      subrogation.superUserCustomerId,
      subrogation.surrogate,
      subrogation.surrogateCustomerId,
    );
  }

  onCancel() {
    this.dialogRef.close();
  }

  cancelSubrogation() {
    this.subrogationService.cancelSubrogation(this.subrogation).subscribe(() => {
      this.dialogRef.close();
    });
  }
}
